// ─────────────────────────────────────────────────────────────────────────────
// DEV_BYPASS_TEST_MODE
// ─────────────────────────────────────────────────────────────────────────────
// Temporary, environment-scoped bypass for billing / Stripe / trial / paywall
// gating so the modern spouse / in-law relationship engine can be tested
// without depending on the live checkout pipeline.
//
// HARD ISOLATION:
//   - Active ONLY when window.location.hostname contains "deploypad.app".
//   - Production hosts (kinscircleapp.com, www.kinscircleapp.com, custom
//     domains, anything that does NOT contain "deploypad.app") are
//     unaffected — every billing / Stripe / auth code path runs exactly as
//     it does today on production.
//   - SSR / non-window environments → returns false (safe default).
//
// SCOPE:
//   - Does NOT auto-authenticate. The user MUST still sign in with a real
//     Supabase session. This is a billing/membership bypass, not an auth
//     bypass.
//   - When an authenticated user is on a deploypad.app host, AuthContext
//     synthesizes a temporary active "annual_founding" subscription so
//     `isPaid`, `isMember`, paywall gates, and trial gating all read as
//     unlocked — the family tree + relationship engine become reachable
//     end-to-end without going through Stripe.
//   - Does NOT mutate the `profiles` table. Nothing is persisted. The
//     synthesized subscription lives entirely in the React context for
//     the lifetime of the page.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns true ONLY when running on a deploypad.app dev/staging host.
 * Production domains return false.
 */
export const isDevBypassHost = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const host = window.location.hostname || '';
    return host.toLowerCase().includes('deploypad.app');
  } catch {
    return false;
  }
};

/**
 * Single-source-of-truth flag used across the app. Evaluated once per call
 * (cheap, no caching needed) so HMR / route changes pick up the correct
 * value without a hard reload.
 */
export const DEV_BYPASS_TEST_MODE = isDevBypassHost();

/**
 * Shape of the synthesized "active subscription" object the bypass injects
 * into the auth context. Mirrors only the fields the UI reads — never
 * written to the database, never sent to Stripe.
 */
export interface DevBypassSubscription {
  active: true;
  plan: 'annual_founding';
  source: 'dev-bypass';
  hostname: string;
  startedAt: string;
}

/**
 * Build a fresh in-memory subscription object. Called by AuthContext when
 * the bypass is active AND the user is authenticated.
 */
export const buildDevBypassSubscription = (): DevBypassSubscription => ({
  active: true,
  plan: 'annual_founding',
  source: 'dev-bypass',
  hostname: typeof window !== 'undefined' ? window.location.hostname : 'unknown',
  startedAt: new Date().toISOString(),
});

/**
 * Console banner so it's obvious in dev that the bypass is active. Called
 * once per session from AuthContext.
 */
export const logDevBypassBanner = (): void => {
  if (!DEV_BYPASS_TEST_MODE) return;
  try {
    // eslint-disable-next-line no-console
    console.warn(
      '%c[DEV_BYPASS_TEST_MODE]%c billing / Stripe / trial / paywall gating is BYPASSED on this host (' +
        (typeof window !== 'undefined' ? window.location.hostname : '?') +
        '). Production hosts are NOT affected.',
      'background:#f59e0b;color:#000;font-weight:bold;padding:2px 6px;border-radius:3px;',
      'color:#f59e0b;',
    );
  } catch {
    /* ignore */
  }
};

/**
 * ─── DEV DEBUG UI VISIBILITY GATE ────────────────────────────────────────
 * Single source of truth for "should developer/debug/repair UI render?".
 *
 * Returns true ONLY when BOTH conditions hold:
 *   1. running on a deploypad.app dev/staging host (DEV_BYPASS_TEST_MODE)
 *   2. the authenticated user is staff (owner OR admin role)
 *
 * Used to gate:
 *   • Graph Debug Panel
 *   • "Repair spouse anchor" / "Repair spouse / partner link" buttons
 *   • diagnostic tables, viewer-mapping logs, placement diagnostics
 *   • edge-insert diagnostics, spouse-rendering diagnostics
 *   • repair banners and any "STAFF" chips/badges
 *   • the dev role-resolution diagnostic in AddGraphMemberModal
 *
 * IMPORTANT — this is a PURE VISIBILITY GATE. The underlying repair
 * engines (repairSpouseEdge, viewer mapping repair, anchor repair,
 * relationship repair, couple rendering, runAllReconciliation, the
 * background ResilienceMonitor, console diagnostics, etc.) ALL still
 * run internally. Only the UI exposure is suppressed on production.
 *
 * Production hosts (kinscircleapp.com, www.kinscircleapp.com, every
 * non-deploypad host) → ALWAYS returns false, no matter the role.
 */
export const isDevDebugVisible = (isStaff: boolean | null | undefined): boolean =>
  DEV_BYPASS_TEST_MODE && !!isStaff;
